import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { PlusIcon, UploadIcon, WorldIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'

import { API } from '../../api/apis.config'
import { getCountriesList, getCountriesListQueryKey } from '../../api/countries/get-countries-list'
import CountryFlag from '../../components/elements/country-flag'
import DynamicDataTable from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import useTranslate from '../../lib/hooks/use-translate'
import { IDTypeEnumMapping } from '../../lib/types/enums/common/id-type.enum'
import { VATOptionEnumMapping } from '../../lib/types/enums/country/vat-option.enum'
import { Country } from '../../lib/types/models/country/country'

const CountryListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const { t } = useTranslate()

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const columns: Column<Country>[] = [
    {
      accessorKey: 'name',
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) => {
        if (!countryList) return '-'
        return <CountryFlag code={row.original.code} label={row.original.name} />
      }
    },
    {
      accessorKey: 'vat',
      header: 'TVA',
      enableSorting: true,
      cell: ({ row }) => VATOptionEnumMapping[row.original.vat]
    },
    {
      accessorKey: 'position',
      header: 'Position',
      enableSorting: true
    },
    {
      accessorKey: 'requiredDocuments',
      header: 'Documents requis',
      enableSorting: true,
      cell: ({ row }) => {
        if (!row.original.requiredDocuments || row.original.requiredDocuments.length === 0)
          return '-'

        return row.original.requiredDocuments
          .map((doc) => t(IDTypeEnumMapping[doc].title))
          .join(', ')
      }
    }
  ]

  return (
    <div>
      <UserNavBar title={'Pays'} icon={WorldIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Country, null>
        title={'Tous les pays'}
        deleteMessage={{ name: 'pays', attributeName: 'name' }}
        columns={columns}
        selectable
        allowSearchParam
        onRowClick={(country, event) =>
          navigateWithEvent(Paths.COUNTRY_UPDATE_PAGE.replace(':id', country.id.toString()), event)
        }
        endpoint={API.countries.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Créer un nouveau pays',
            fn: () => navigate(Paths.COUNTRY_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (rows) => {
              const ids = rows.map((row) => row.id)
              downloadDocuments({
                endpoint: API.countries.exportExcel.url,
                ids
              })
            }
          }
        ]}
      />
    </div>
  )
}

export default CountryListPage
